/**
 * Lightweight JWT Decoder Utility
 *
 * TASK-085: Account Context Management + Password Authentication
 *
 * This utility decodes JWT tokens without requiring external dependencies.
 *
 * IMPORTANT SECURITY NOTES:
 * - This only DECODES tokens, it does NOT VALIDATE them
 * - Token validation (signature verification) happens on the backend
 * - Never trust client-side token data for security decisions
 * - Use this only for reading claims (user_id, account_id, etc.)
 *
 * @module utils/jwtDecoder
 */

/**
 * JWT Payload interface matching backend token structure
 * See: backend/services/auth-service/security_base.py
 */
export interface JWTPayload {
  // Core identity claims
  user_id: string;
  user_type: string;  // 'demo' | 'advisor' | 'client'
  role?: 'admin' | 'advisor' | 'viewer' | 'compliance_officer';  // User role for RBAC (TASK-264)

  // Account/tenant claims (multi-tenancy)
  account_id: string;
  account_name: string;
  email: string;

  // User profile claims
  first_name?: string;
  last_name?: string;

  // Demo-specific claims
  demo_scenario?: string;  // 'wealth-mgmt-demo' | 'family-office-demo' | 'institutional-demo'

  // Per-tenant UI feature flags (TASK-428 cockpit registry). Empty/absent = permissive.
  features?: Record<string, boolean>;

  // Token metadata
  issued_at: string;      // ISO 8601 timestamp
  expires_at: string;     // ISO 8601 timestamp
  security_level?: string;
  swiss_compliance?: boolean;
}

/**
 * Decode a JWT token and extract its payload claims.
 *
 * JWT Structure: header.payload.signature
 * - Header: Algorithm and token type (base64url encoded)
 * - Payload: Claims/data (base64url encoded) ← This is what we decode
 * - Signature: Verification signature (validated server-side only)
 *
 * @param token - JWT token string (Bearer token without "Bearer " prefix)
 * @returns Decoded JWT payload with account and user information
 * @throws Error if token format is invalid or decoding fails
 *
 * @example
 * ```typescript
 * const token = localStorage.getItem('auth_token');
 * if (token) {
 *   try {
 *     const payload = decodeJWT(token);
 *     console.log('User:', payload.user_id);
 *     console.log('Account:', payload.account_id);
 *     console.log('Email:', payload.email);
 *   } catch (error) {
 *     console.error('Invalid token:', error);
 *     // Handle invalid token (logout, redirect, etc.)
 *   }
 * }
 * ```
 */
export function decodeJWT(token: string): JWTPayload {
  try {
    // JWT format: header.payload.signature
    const parts = token.split('.');

    if (parts.length !== 3) {
      throw new Error(
        `Invalid JWT format: expected 3 parts (header.payload.signature), got ${parts.length}`
      );
    }

    // Extract the payload (middle part)
    const payloadBase64Url = parts[1];

    // Decode base64url to base64
    // Base64url differs from base64 in these characters:
    // - Replace '-' with '+'
    // - Replace '_' with '/'
    // - No padding ('=') in base64url
    const base64 = payloadBase64Url
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    // Decode base64 to JSON string
    // atob() is a browser built-in for base64 decoding
    const jsonString = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => {
          // Convert each character to percent-encoded UTF-8
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );

    // Parse JSON to object
    const payload = JSON.parse(jsonString) as JWTPayload;

    // Basic payload validation
    if (!payload.user_id || !payload.account_id) {
      throw new Error(
        'Invalid JWT payload: missing required claims (user_id, account_id)'
      );
    }

    return payload;

  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Failed to decode JWT token: ${error.message}`);
    }
    throw new Error('Failed to decode JWT token: Unknown error');
  }
}

/**
 * Check if a JWT token is expired based on the expires_at claim.
 *
 * NOTE: This is a client-side convenience check only.
 * The backend MUST also validate token expiry for security.
 *
 * @param token - JWT token string
 * @returns true if token is expired, false otherwise
 *
 * @example
 * ```typescript
 * const token = localStorage.getItem('auth_token');
 * if (token && isTokenExpired(token)) {
 *   console.log('Token expired, please login again');
 *   // Clear token and redirect to login
 * }
 * ```
 */
export function isTokenExpired(token: string): boolean {
  try {
    const payload = decodeJWT(token);

    if (!payload.expires_at) {
      // If no expiry claim, consider it expired for safety
      return true;
    }

    const expiryTime = new Date(payload.expires_at).getTime();
    const currentTime = Date.now();

    return currentTime >= expiryTime;

  } catch (error) {
    // If token is invalid or can't be decoded, consider it expired
    return true;
  }
}

/**
 * Get time remaining until token expiry in milliseconds.
 *
 * @param token - JWT token string
 * @returns Milliseconds until expiry, or 0 if expired/invalid
 *
 * @example
 * ```typescript
 * const token = localStorage.getItem('auth_token');
 * if (token) {
 *   const msRemaining = getTokenTimeRemaining(token);
 *   const hoursRemaining = Math.floor(msRemaining / 1000 / 60 / 60);
 *   console.log(`Token expires in ${hoursRemaining} hours`);
 * }
 * ```
 */
export function getTokenTimeRemaining(token: string): number {
  try {
    const payload = decodeJWT(token);

    if (!payload.expires_at) {
      return 0;
    }

    const expiryTime = new Date(payload.expires_at).getTime();
    const currentTime = Date.now();
    const remaining = expiryTime - currentTime;

    return remaining > 0 ? remaining : 0;

  } catch (error) {
    return 0;
  }
}
